import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SHOW_ACTIVITY_LOG } from "./features";
import { ActivityPanel } from "../activity/ActivityPanel";
import { WwwPortalNavBar } from "./WwwPortalNavBar";
import "./mobile-menu.css";

interface MobileMenuProps {
    onLogout: () => void;
    onLanguageChange: (lang: "sv" | "en") => void;
    currentLanguage: string;
    isMobileApp?: boolean;
    onKartlagerClick?: () => void;
    /** When false, hide Admin / change password / language / logout (non-map www routes). */
    showMapOnlyActions?: boolean;
}

export const MobileMenu = ({
    onLogout,
    onLanguageChange,
    currentLanguage,
    isMobileApp = false,
    onKartlagerClick,
    showMapOnlyActions = false
}: MobileMenuProps): JSX.Element => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <>
            <button
                type="button"
                className="mobile-menu__toggle"
                onClick={handleToggle}
                aria-label={t("app.menu.toggle")}
                aria-expanded={isOpen}
            >
                <span className="mobile-menu__icon">
                    <span></span>
                    <span></span>
                    <span></span>
                </span>
            </button>

            {isOpen &&
                createPortal(
                    <>
                        <div
                            className="mobile-menu__overlay"
                            onClick={handleClose}
                            aria-hidden="true"
                        />
                        <div className="mobile-menu__drawer">
                            <div className="mobile-menu__header">
                                <h2>{t("app.menu.title")}</h2>
                                <button
                                    type="button"
                                    className="mobile-menu__close"
                                    onClick={handleClose}
                                    aria-label={t("app.menu.close")}
                                >
                                    ✕
                                </button>
                            </div>

                            <nav className="mobile-menu__nav">
                                <WwwPortalNavBar
                                    surface="www_map"
                                    className="cr-global-nav--stacked mobile-menu__global-nav"
                                />

                                {showMapOnlyActions ? (
                                    <>
                                        {isMobileApp ? (
                                            <button
                                                type="button"
                                                className="mobile-menu__link"
                                                onClick={() => {
                                                    handleClose();
                                                    const adminUrl = `${window.location.origin}/admin`;
                                                    if (typeof (window as any).cfOpenInBrowser === "function") {
                                                        (window as any).cfOpenInBrowser(adminUrl);
                                                    } else {
                                                        console.warn(
                                                            "[MobileMenu] cfOpenInBrowser not available, using window.open fallback"
                                                        );
                                                        window.open(adminUrl, "_blank");
                                                    }
                                                }}
                                            >
                                                {t("app.menu.admin")}
                                            </button>
                                        ) : (
                                            <Link to="/admin" className="mobile-menu__link" onClick={handleClose}>
                                                {t("app.menu.admin")}
                                            </Link>
                                        )}
                                        <Link to="/change-password" className="mobile-menu__link" onClick={handleClose}>
                                            {t("app.changePassword.nav")}
                                        </Link>
                                        <div className="mobile-menu__section">
                                            <p className="mobile-menu__section-title">{t("app.languageSwitch.label")}</p>
                                            <div className="mobile-menu__lang-buttons">
                                                <button
                                                    type="button"
                                                    className={`mobile-menu__lang-button ${currentLanguage === "sv" || currentLanguage?.startsWith("sv") ? "active" : ""}`}
                                                    onClick={() => {
                                                        onLanguageChange("sv");
                                                        handleClose();
                                                    }}
                                                >
                                                    {t("app.languageSwitch.sv")}
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`mobile-menu__lang-button ${currentLanguage === "en" || currentLanguage?.startsWith("en") ? "active" : ""}`}
                                                    onClick={() => {
                                                        onLanguageChange("en");
                                                        handleClose();
                                                    }}
                                                >
                                                    {t("app.languageSwitch.en")}
                                                </button>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="mobile-menu__logout"
                                            onClick={() => {
                                                onLogout();
                                                handleClose();
                                            }}
                                        >
                                            {t("app.logout")}
                                        </button>
                                    </>
                                ) : null}

                                {onKartlagerClick && (
                                    <button
                                        type="button"
                                        className="mobile-menu__link"
                                        onClick={() => {
                                            onKartlagerClick();
                                            handleClose();
                                        }}
                                    >
                                        {t("map.layers.title")}
                                    </button>
                                )}
                            </nav>

                            {SHOW_ACTIVITY_LOG && (
                                <div className="mobile-menu__activity">
                                    <ActivityPanel />
                                </div>
                            )}
                        </div>
                    </>,
                    document.body
                )}
        </>
    );
};